import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BookOpen,
  Database,
  KeyRound,
  Monitor,
  Moon,
  Pause,
  Play,
  Plug,
  ShieldCheck,
  Sparkles,
  User,
  X,
  type LucideIcon,
} from 'lucide-react'
import type { TenantInstanceClaude } from '@omni/instance'
import { activeInstance } from '../instances'
import './claude.css'

// How your Command Centre is built (Sam, 10 Oct 2026): "the guys I'm dealing
// with don't understand database, Claude Code, Command Centre, GitHub, vault.
// One click, visually show them what this all means, how it's set up for
// them, why it matters, and what the AI is actually doing."
//
// One picture of the parts, each with what it is in plain words, its real
// name in small print, and why it matters. "Watch Claude do a job" lights the
// parts up in the order Claude actually touches them, using the customer's own
// example question. Words come from the instance's `claude.built` settings,
// with honest defaults, so every customer sees their own Command Centre.

type PartId = 'you' | 'screens' | 'claude' | 'handbook' | 'vault' | 'memory' | 'connections' | 'night' | 'yes'

interface Part {
  id: PartId
  Icon: LucideIcon
  name: string
  tech?: string
  what: string
  why: string
  list?: string[]
}

function partsFor(c: TenantInstanceClaude): Part[] {
  const b = c.built ?? {}
  const product = activeInstance.copy?.productName ?? (__FRONT_DOOR__ ? activeInstance.displayName : 'Command Centre')
  return [
    {
      id: 'you',
      Icon: User,
      name: 'You',
      what: 'You look at the screens and you ask Claude. That is all you ever have to do.',
      why: 'Nothing technical is on your side of the glass.',
    },
    {
      id: 'screens',
      Icon: Monitor,
      name: 'The screens',
      tech: product,
      what: 'The pages you are looking at now. They show what is going on: your list, your clients, your mail.',
      why: 'One place to look. They only show; they never change anything by themselves.',
    },
    {
      id: 'claude',
      Icon: Sparkles,
      name: 'Claude, your assistant',
      tech: 'Claude Code, at claude.ai/code',
      what: 'Where you ask for things in plain words. Claude reads, thinks, drafts and updates your records.',
      why: 'It does the work. You check it and say yes.',
    },
    {
      id: 'handbook',
      Icon: BookOpen,
      name: 'Claude’s handbook',
      tech: `Your folder on GitHub: ${c.folder}`,
      what: 'Everything Claude knows about how your business runs: your voice, your rules, the jobs it can do.',
      why: 'Claude reads it at the start of every chat, so it gets better as the handbook grows. Every change is kept, nothing is lost.',
    },
    {
      id: 'vault',
      Icon: KeyRound,
      name: 'The key safe',
      tech: 'Infisical, an encrypted vault',
      what: 'The passwords and keys to your systems, locked away. Claude borrows a key for one job and never writes it down.',
      why: 'Your keys are never in an email, a chat or a file. Change one here and it changes everywhere.',
    },
    {
      id: 'memory',
      Icon: Database,
      name: 'Your business memory',
      tech: 'Your own database (Supabase)',
      what: 'Where every record lives, in one place that belongs to you.',
      why: 'The screens and Claude read the same facts, so they always agree.',
      list: b.records,
    },
    {
      id: 'connections',
      Icon: Plug,
      name: 'Your connections',
      what: 'The systems you already use, plugged in so your memory stays up to date.',
      why: `You keep working the way you do. ${__FRONT_DOOR__ ? (activeInstance.copy?.productName ?? activeInstance.displayName) : 'The Command Centre'} catches up on its own.`,
      list: b.connections ?? ['Your email', 'The other systems you already use'],
    },
    {
      id: 'night',
      Icon: Moon,
      name: 'The night shift',
      tech: 'Scheduled jobs',
      what: 'Work that runs by itself while you sleep.',
      why: 'You start each day with everything already sorted.',
      list: b.nightly ?? ['Reads what came in today', 'Refreshes your lists'],
    },
    {
      id: 'yes',
      Icon: ShieldCheck,
      name: 'Your yes',
      what: 'Nothing is sent, posted or shown to a client until you say so.',
      why: 'Claude drafts. You decide.',
    },
  ]
}

interface Step {
  lit: PartId[]
  say: string
}

function stepsFor(c: TenantInstanceClaude): Step[] {
  const b = c.built ?? {}
  return [
    { lit: ['you', 'claude'], say: `You ask Claude: “${c.example.ask}”` },
    { lit: ['claude', 'handbook'], say: 'Claude opens its handbook, so it knows your business, your voice and your rules.' },
    { lit: ['claude', 'vault'], say: 'It borrows the key it needs from the key safe, just for this job.' },
    {
      lit: ['claude', 'memory'],
      say: b.reads ?? 'It reads your records in your business memory: the people, the history, what is due.',
    },
    { lit: ['claude', 'you'], say: `It answers you: “${c.example.answer}”` },
    { lit: ['yes', 'you'], say: 'If anything should go out, it waits. You read it and say “send it”, or ask for a change.' },
    { lit: ['memory', 'screens'], say: 'What changed is saved to your memory, and your screens update within a minute.' },
    { lit: ['night', 'connections', 'memory'], say: 'Overnight, the night shift brings in what is new, ready for tomorrow.' },
  ]
}

const STEP_MS = 3200

export function HowItsBuilt({ onClose }: { onClose: () => void }) {
  const c = activeInstance.claude
  const parts = useMemo(() => (c ? partsFor(c) : []), [c])
  const steps = useMemo(() => (c ? stepsFor(c) : []), [c])
  const [step, setStep] = useState<number | null>(null)
  const [playing, setPlaying] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    if (!playing || step === null) return
    timer.current = setTimeout(() => {
      if (step >= steps.length - 1) setPlaying(false)
      else setStep(step + 1)
    }, STEP_MS)
    return () => {
      if (timer.current) clearTimeout(timer.current)
    }
  }, [playing, step, steps.length])

  if (!c) return null
  const lit = new Set(step === null ? [] : steps[step].lit)
  const by = Object.fromEntries(parts.map((p) => [p.id, p])) as Record<PartId, Part>

  const play = () => {
    if (playing) {
      setPlaying(false)
      return
    }
    setStep(step === null || step >= steps.length - 1 ? 0 : step)
    setPlaying(true)
  }

  const card = (id: PartId, extra = '') => {
    const p = by[id]
    const on = lit.has(id)
    const dim = step !== null && !on
    return (
      <div key={id} className={`oc-hb-part oc-hb-${id} ${extra} ${on ? 'is-lit' : ''} ${dim ? 'is-dim' : ''}`}>
        <div className="oc-hb-part-head">
          <span className="oc-hb-icon">
            <p.Icon size={17} />
          </span>
          <span className="min-w-0">
            <b>{p.name}</b>
            {p.tech ? <small>{p.tech}</small> : null}
          </span>
        </div>
        <p className="oc-hb-what">{p.what}</p>
        {p.list?.length ? (
          <ul className="oc-hb-list">
            {p.list.map((t) => (
              <li key={t}>{t}</li>
            ))}
          </ul>
        ) : null}
        <p className="oc-hb-why">
          <span>Why it matters</span> {p.why}
        </p>
      </div>
    )
  }

  return (
    <div className="oc-hb" role="dialog" aria-label={__FRONT_DOOR__ ? `How ${activeInstance.copy?.productName ?? activeInstance.displayName} is built` : 'How your Command Centre is built'}>
      <div className="oc-hb-inner">
        <header className="oc-hb-head">
          <div className="min-w-0">
            <p className="oc-hb-kicker">{__FRONT_DOOR__ ? `How ${activeInstance.copy?.productName ?? activeInstance.displayName} is built` : 'How your Command Centre is built'}</p>
            <h2>You only ever touch two parts: the screens and Claude.</h2>
            <p className="oc-hb-sub">Everything else works behind the glass, so you never have to think about it. Here is what each part is, and why it is there.</p>
          </div>
          <button type="button" className="oc-hb-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="oc-hb-player">
          <button type="button" className="oc-hb-play" onClick={play}>
            {playing ? <Pause size={15} /> : <Play size={15} />}
            {playing ? 'Pause' : step === null ? 'Watch Claude do a job' : step >= steps.length - 1 ? 'Watch again' : 'Carry on'}
          </button>
          <div className="oc-hb-say" aria-live="polite">
            {step === null ? (
              <span className="oc-hb-say-idle">Press play to see, step by step, what Claude actually does when you ask it something.</span>
            ) : (
              <>
                <span className="oc-hb-stepno">
                  {step + 1} of {steps.length}
                </span>
                <span>{steps[step].say}</span>
              </>
            )}
          </div>
          {step !== null && (
            <div className="oc-hb-dots">
              {steps.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Step ${i + 1}`}
                  className={i === step ? 'is-on' : ''}
                  onClick={() => {
                    setPlaying(false)
                    setStep(i)
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="oc-hb-map">
          <div className="oc-hb-band">
            <p className="oc-hb-band-label">What you see and use</p>
            <div className="oc-hb-row oc-hb-row-3">
              {card('screens')}
              {card('you', 'oc-hb-you')}
              {card('claude')}
            </div>
          </div>
          <div className="oc-hb-band oc-hb-band-back">
            <p className="oc-hb-band-label">Behind the glass: set up for you, looked after by us</p>
            <div className="oc-hb-row oc-hb-row-3">
              {card('connections')}
              {card('memory', 'oc-hb-core')}
              {card('handbook')}
            </div>
            <div className="oc-hb-row oc-hb-row-3">
              {card('night')}
              {card('yes')}
              {card('vault')}
            </div>
          </div>
        </div>

        <p className="oc-hb-foot">
          The short version: the screens show, Claude does, your memory remembers, the handbook teaches, the safe protects,
          and nothing leaves without your yes.
        </p>
      </div>
    </div>
  )
}

// Opened from anywhere (the Claude panel, a page button) by a window event,
// mounted once in the chrome next to the Claude panel.
const OPEN_EVENT = 'omni-claude:how-built'
export function openHowItsBuilt() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

export function HowItsBuiltHost() {
  // A link ending #how-its-built opens it straight away (for an email or a
  // walkthrough: one click from the inbox to the picture).
  const [open, setOpen] = useState(() => {
    try {
      return window.location.hash === '#how-its-built'
    } catch {
      return false
    }
  })
  useEffect(() => {
    const on = () => setOpen(true)
    window.addEventListener(OPEN_EVENT, on)
    return () => window.removeEventListener(OPEN_EVENT, on)
  }, [])
  if (!open || !activeInstance.claude) return null
  return (
    <HowItsBuilt
      onClose={() => {
        setOpen(false)
        if (window.location.hash === '#how-its-built') history.replaceState(null, '', window.location.pathname + window.location.search)
      }}
    />
  )
}
